import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { AlertCircle, Loader2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { auth, db, functions } from "../../../firebase";

export const Route = createFileRoute("/invite/$email/")({
  component: RouteComponent,
});

interface InviteData {
  email: string;
  invitedBy: string;
  invitedAt: any;
  status: string;
  role?: string;
  existingUserId?: string;
  existingUserName?: string;
}

function RouteComponent() {
  const { email } = Route.useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const inviteDoc = await getDoc(doc(db, "UserInvites", email));

        if (!inviteDoc.exists()) {
          setError("Invite not found or has already been used.");
          return;
        }

        const data = inviteDoc.data() as InviteData;
        if (data.status !== "pending") {
          setError("This invite has already been used.");
          return;
        }

        setInviteData(data);

        // Pre-fill name if this is an existing user
        if (data.existingUserName) {
          setName(data.existingUserName);
        }
      } catch (err) {
        console.error("Error checking invite:", err);
        setError("Failed to verify invite. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    checkInvite();
  }, [email]);

  const validateForm = (): boolean => {
    // Skip name validation for existing users with pre-filled names
    if (!inviteData?.existingUserId && !name.trim()) {
      setError("Please enter your name.");
      return false;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return false;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }

    return true;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (inviteData?.existingUserId) {
        // This is an existing authless user (customer or agency) - set up their credentials
        const updatePasswordCallable = httpsCallable(functions, "updateUserPassword");
        const result = await updatePasswordCallable({
          userId: inviteData.existingUserId,
          password: password,
        });

        const resultData = result.data as { success: boolean; error?: string };
        if (!resultData.success) {
          setError("Failed to set up login credentials. Please try again.");
          return;
        }

        // sign in so user have appropriate access and permissions
        await signInWithEmailAndPassword(auth, email, password);

        // Update the existing user's name if provided
        if (name.trim() && name.trim() !== inviteData.existingUserName) {
          await updateDoc(doc(db, "Users", inviteData.existingUserId), {
            name: name.trim(),
            updatedAt: serverTimestamp(),
          });
        }

        // Mark invite as accepted
        await updateDoc(doc(db, "UserInvites", email), {
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });

        toast.success("Account setup complete! You can now log in.");

        navigate({ to: "/login" });
      } else {
        // Create new Firebase auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Add user to Users collection
        await setDoc(doc(db, "Users", user.uid), {
          uid: user.uid,
          email: email,
          name: name.trim(),
          createdAt: new Date(),
          type: inviteData?.role || "customer",
        });

        // Mark invite as accepted
        await updateDoc(doc(db, "UserInvites", email), {
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });

        navigate({ to: "/login" });
      }
    } catch (err: any) {
      console.error("Error creating account:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists.");
      } else if (err.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
            <p className="text-muted-foreground mt-4 text-center">Verifying invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <UserPlus className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle>
            {inviteData?.existingUserId ? "Set Up Your Login" : "Complete Your Registration"}
          </CardTitle>
          <CardDescription>
            {inviteData?.existingUserId
              ? "Create a password to access your account."
              : `You have been invited to join GetHandle as ${inviteData?.role === "agency" ? "an agency" : "a user"}. Please complete your account setup.`}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={email} disabled className="bg-gray-100" />
            </div>

            {inviteData?.existingUserId && inviteData?.existingUserName ? (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  disabled
                  className="bg-gray-100"
                  title="This is your existing name on record"
                />
                <p className="text-muted-foreground text-xs">
                  This is your existing name on record
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password (min. 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
